import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, TooltipProps } from 'recharts';
import { Select } from 'antd';
import styles from './OrdersByCategoryChart.module.scss';

interface OrdersByCategoryData {
  category: string;
  orders: number;
}

interface OrdersByCategoryChartProps {
  data: OrdersByCategoryData[];
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: '#171f20',
        border: '1px solid #dab94f',
        borderRadius: '4px',
        padding: '10px',
        color: '#e9e9e9'
      }}>
        <p style={{ margin: '0 0 5px 0' }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '0', color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const OrdersByCategoryChart: React.FC<OrdersByCategoryChartProps> = ({ data }) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['all']);

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(data.map(item => item.category)));
    return [
      { label: 'Sve kategorije', value: 'all' },
      ...uniqueCategories.map(category => ({ label: category, value: category }))
    ];
  }, [data]);

  const filteredData = useMemo(() => {
    if (selectedCategories.includes('all')) {
      return data;
    }
    return data.filter(item => selectedCategories.includes(item.category));
  }, [data, selectedCategories]);

  const handleCategoryChange = (value: string[]) => {
    if (value.includes('all')) {
      setSelectedCategories(['all']);
    } else {
      setSelectedCategories(value);
    }
  };

  return (
    <div className={styles.chartContainer}>
      <div className={styles.header}>
        <h3>Narudžbine po kategoriji</h3>
        <Select
          mode="multiple"
          value={selectedCategories}
          onChange={handleCategoryChange}
          options={categories}
          className={styles.select}
          placeholder="Izaberite kategorije"
          maxTagCount="responsive"
          style={{ minWidth: '200px' }}
        />
      </div>
      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
            <XAxis dataKey="category" stroke="#e9e9e9" />
            <YAxis stroke="#e9e9e9" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="orders" name="Narudžbine" fill="#dab94f" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OrdersByCategoryChart; 